import { supabase } from '@/lib/supabaseClient';
import { JOB_PHASES, PHASE_MAX_INDEX, todayLabel } from '@/lib/jobPhases';
import * as pqi from '@/lib/pqiTracker';

/* ----------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------- */
export const formatUsd = (value, opts = {}) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: opts.fraction ?? 0,
    minimumFractionDigits: opts.fraction ?? 0,
  }).format(safe);
};

export const parseContractorDate = (input) => {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed || /^tbd$/i.test(trimmed)) return null;
  const direct = Date.parse(trimmed);
  if (!Number.isNaN(direct)) return new Date(direct);
  const fallback = Date.parse(`${trimmed}, ${new Date().getFullYear()}`);
  if (!Number.isNaN(fallback)) return new Date(fallback);
  return null;
};

const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

/* ----------------------------------------------------------------------------
 * Job margin quick-compute (from embedded job record, no extra queries)
 *
 * Returns { actualCost, totalRevenue, marginPct } using:
 *   - revenue   = contract + co_total
 *   - costBasis = materials_spent (sum of material_log) + optional laborCost
 * ------------------------------------------------------------------------- */
export const computeEmbeddedJobMargin = (job, { laborCost = 0 } = {}) => {
  if (!job) return { actualCost: 0, totalRevenue: 0, marginPct: 0 };
  const contract = Number(job.contract) || 0;
  const coTotal = Number(job.co_total) || 0;
  const totalRevenue = contract + coTotal;
  // Prefer materials_spent (already rolled up on every material log mutation).
  // Fall back to summing material_log if it's populated but rollup isn't.
  let materials = Number(job.materials_spent) || 0;
  if (!materials && Array.isArray(job.material_log)) {
    materials = job.material_log.reduce(
      (s, r) => s + (Number(r?.amount) || 0),
      0
    );
  }
  const actualCost = materials + (Number(laborCost) || 0);
  const marginPct =
    totalRevenue > 0 ? ((totalRevenue - actualCost) / totalRevenue) * 100 : 0;
  return { actualCost, totalRevenue, marginPct };
};

/* ----------------------------------------------------------------------------
 * Invoice status helpers
 * ------------------------------------------------------------------------- */
export const deriveInvoiceStatus = (invoice) => {
  if (!invoice) return 'upcoming';
  if (invoice.status === 'paid') return 'paid';
  const due = parseContractorDate(invoice?.due);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due) {
    if (due < today) return 'overdue';
    const sevenDays = new Date(today.getTime());
    sevenDays.setDate(sevenDays.getDate() + 7);
    if (due <= sevenDays) return 'due';
  }
  return invoice?.status || 'upcoming';
};

export const invoiceTotals = (invoices = []) => {
  let total = 0;
  let paid = 0;
  let overdue = 0;
  let upcoming = 0;
  for (const inv of invoices) {
    const amount = Number(inv?.amount) || 0;
    total += amount;
    const s = deriveInvoiceStatus(inv);
    if (s === 'paid') paid += amount;
    else if (s === 'overdue') overdue += amount;
    else upcoming += amount;
  }
  return { total, paid, overdue, upcoming, outstanding: total - paid };
};

/**
 * Flags a job whose invoice schedule has drifted from its contract value.
 * Nothing keeps a job's invoice line items in sync with its contract/change-
 * order total after they're created — edit the contract or add a change
 * order and the existing invoices just sit there. Found live 2026-09-12
 * (John caught a $440 gap on a job by eyeballing Overview vs. Invoices):
 * several jobs had drifted, from trivial rounding up to a $6,020 gap where
 * change-order work had no invoice covering it at all. This is a silent
 * risk of leaving real money un-billed (or, the other direction, invoicing
 * more than the contract), so both tabs surface a warning instead of
 * requiring someone to compare tabs by hand.
 * Skips T&M jobs (no fixed contract to reconcile against) and jobs with no
 * invoices yet (nothing to compare — a brand-new job isn't "wrong").
 * Tolerates $1 of rounding.
 */
export const invoiceReconciliation = (job) => {
  const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
  const isTM = (job?.job_type || 'fixed') === 'tm';
  if (isTM || invoices.length === 0) {
    return { warn: false, gap: 0, contractValue: 0, invoicedTotal: 0 };
  }
  const contractValue = (Number(job?.contract) || 0) + (Number(job?.co_total) || 0);
  const invoicedTotal = invoiceTotals(invoices).total;
  const gap = Math.round((contractValue - invoicedTotal) * 100) / 100;
  return { warn: Math.abs(gap) >= 1, gap, contractValue, invoicedTotal };
};

/* ----------------------------------------------------------------------------
 * Stats / filtering
 * ------------------------------------------------------------------------- */
export const computeJobsStats = (jobs = []) => {
  let pipeline = 0;
  let collected = 0;
  let outstanding = 0;
  let count = 0;
  for (const job of jobs) {
    const contract = Number(job?.contract) || 0;
    const co = Number(job?.co_total) || 0;
    pipeline += contract + co;
    const t = invoiceTotals(job?.invoices || []);
    collected += t.paid;
    outstanding += t.outstanding;
    count += 1;
  }
  return { pipeline, collected, outstanding, count };
};

export const applyJobFilter = (
  jobs = [],
  { search = '', sort = 'phase-desc' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  let list = jobs.slice();
  if (q) {
    list = list.filter((j) => {
      const hay = [j?.name, j?.client, j?.address, j?.job_type, j?.status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }
  const cmp = (a, b) => {
    switch (sort) {
      case 'name-asc':
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      case 'name-desc':
        return String(b?.name || '').localeCompare(String(a?.name || ''));
      case 'value-desc':
        return (Number(b?.contract) || 0) - (Number(a?.contract) || 0);
      case 'phase-asc':
        return (Number(a?.phase) || 0) - (Number(b?.phase) || 0);
      case 'phase-desc':
      default:
        return (Number(b?.phase) || 0) - (Number(a?.phase) || 0);
    }
  };
  return list.sort(cmp);
};

/* ----------------------------------------------------------------------------
 * Reads
 * ------------------------------------------------------------------------- */
export const JOB_FIELDS =
  'id,name,client,client_id,address,contract,co_total,phase,status,started,est_end,job_type,materials_spent,notes,change_orders,invoices,receipts,material_log,created_at';

export const fetchJobsAndClients = async () => {
  const [jobsRes, clientsRes] = await Promise.all([
    supabase
      .from('jobs')
      .select(JOB_FIELDS)
      .order('created_at', { ascending: false }),
    supabase
      .from('clients')
      .select('id,name,phone,email,address,type,ltv,lead_value,lead_type')
      .order('name', { ascending: true }),
  ]);
  if (jobsRes.error) throw jobsRes.error;
  if (clientsRes.error) throw clientsRes.error;
  return { jobs: jobsRes.data || [], clients: clientsRes.data || [] };
};

export const fetchJob = async (id) => {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
};

/* ----------------------------------------------------------------------------
 * Writes
 * ------------------------------------------------------------------------- */
const sanitizeJobPayload = (input = {}, { partial = false } = {}) => {
  const out = {};
  const passthroughStrings = [
    'name',
    'client',
    'address',
    'started',
    'est_end',
    'job_type',
    'status',
  ];
  for (const k of passthroughStrings) {
    if (k in input) {
      const v = input[k];
      out[k] = v === null || v === undefined ? null : String(v).trim();
    }
  }
  if ('client_id' in input) {
    out.client_id = input.client_id || null;
  }
  for (const k of ['contract', 'co_total', 'materials_spent', 'phase']) {
    if (k in input) {
      const n = Number(input[k]);
      out[k] = Number.isFinite(n) ? n : 0;
    }
  }
  if ('invoices' in input) out.invoices = Array.isArray(input.invoices) ? input.invoices : [];
  if ('change_orders' in input)
    out.change_orders = Array.isArray(input.change_orders) ? input.change_orders : [];
  if ('notes' in input) out.notes = Array.isArray(input.notes) ? input.notes : [];
  if ('receipts' in input) out.receipts = Array.isArray(input.receipts) ? input.receipts : [];
  if ('material_log' in input)
    out.material_log = Array.isArray(input.material_log) ? input.material_log : [];
  if (!partial && !out.status) out.status = 'active';
  return out;
};

export const updateJob = async (id, updates) => {
  const safe = sanitizeJobPayload(updates, { partial: true });
  const { data, error } = await supabase
    .from('jobs')
    .update(safe)
    .eq('id', id)
    .select(JOB_FIELDS)
    .single();
  if (error) throw error;
  return data;
};

export const deleteJob = async (id) => {
  const { error } = await supabase.from('jobs').delete().eq('id', id);
  if (error) throw error;
  return true;
};

/* ----------------------------------------------------------------------------
 * Archive lifecycle
 *
 * A job is considered "archived" when its status is 'closed' (legacy data) OR
 * 'completed' (new explicit completes). The Active Jobs board hides these by
 * default and the Archived tab surfaces them. We persist status='closed' to
 * stay backward-compatible with existing data and with advancePhase() (which
 * already auto-closes when phase reaches PHASE_MAX_INDEX).
 * ------------------------------------------------------------------------- */
export const isJobArchived = (job) =>
  job?.status === 'closed' || job?.status === 'completed';

export const markJobComplete = async (job) => {
  if (!job?.id) throw new Error('Missing job id');
  const note = {
    date: todayLabel(),
    text: 'Job marked complete \u2014 archived',
    type: 'milestone',
  };
  const updates = {
    status: 'closed',
    phase: PHASE_MAX_INDEX,
    notes: [...(Array.isArray(job.notes) ? job.notes : []), note],
  };
  const updated = await updateJob(job.id, updates);
  // Fire margin snapshot + job-closed event so PQI captures the close.
  try {
    const { actualCost, totalRevenue, marginPct } =
      computeEmbeddedJobMargin(updated);
    pqi.trackMarginSnapshot({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
      phasesCompleted: PHASE_MAX_INDEX,
      trigger: 'job_closed',
    });
    pqi.trackJobClosed({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
    });
  } catch (_) {
    /* PQI is best-effort — don't fail the mark-complete on telemetry errors. */
  }
  return updated;
};

export const restoreJob = async (job) => {
  if (!job?.id) throw new Error('Missing job id');
  const note = {
    date: todayLabel(),
    text: 'Job restored to active',
    type: 'general',
  };
  const updates = {
    status: 'active',
    notes: [...(Array.isArray(job.notes) ? job.notes : []), note],
  };
  return updateJob(job.id, updates);
};


/* ----------------------------------------------------------------------------
 * Auto-generated invoices for new jobs (40 / 30 / 30 split).
 * ------------------------------------------------------------------------- */
export const buildAutoInvoices = ({ contract, started, estEnd }) => {
  const c = Number(contract) || 0;
  const startDate = parseContractorDate(started);
  const endDate = parseContractorDate(estEnd);

  const fmt = (d) =>
    d
      ? new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(d)
      : 'TBD';

  const milestoneDate = startDate
    ? (() => {
        const d = new Date(startDate.getTime());
        d.setDate(d.getDate() + 30);
        return d;
      })()
    : null;
  const finalDate = endDate
    ? endDate
    : startDate
    ? (() => {
        const d = new Date(startDate.getTime());
        d.setDate(d.getDate() + 60);
        return d;
      })()
    : null;

  return [
    {
      id: genId('INV'),
      due: fmt(startDate),
      desc: 'Deposit 40% \u2014 Due at signing',
      amount: Math.round(c * 0.4),
      status: 'upcoming',
    },
    {
      id: genId('INV'),
      due: fmt(milestoneDate),
      desc: 'Milestone 30% \u2014 Mid-project progress',
      amount: Math.round(c * 0.3),
      status: 'upcoming',
    },
    {
      id: genId('INV'),
      due: fmt(finalDate),
      desc: 'Final 30% \u2014 Project completion',
      amount: c - Math.round(c * 0.4) - Math.round(c * 0.3),
      status: 'upcoming',
    },
  ];
};

export const createJob = async (payload) => {
  const contract = Number(payload?.contract) || 0;
  // T&M jobs bill from the weekly record, not a contract split — no auto
  // 40/30/30 schedule (there is no contract to split).
  const isTM = (payload?.job_type || 'fixed') === 'tm';
  const auto = isTM
    ? []
    : buildAutoInvoices({
        contract,
        started: payload?.started,
        estEnd: payload?.est_end,
      });
  const safe = sanitizeJobPayload({
    name: payload?.name,
    client: payload?.client,
    client_id: payload?.client_id,
    address: payload?.address,
    contract,
    co_total: 0,
    phase: 0,
    status: 'active',
    started: payload?.started || 'TBD',
    est_end: payload?.est_end || 'TBD',
    job_type: payload?.job_type || 'fixed',
    materials_spent: 0,
    invoices: auto,
    change_orders: [],
    notes: [
      {
        date: todayLabel(),
        text: isTM
          ? 'T&M job created \u2014 billing follows the agreement (weekly record); no auto invoice schedule'
          : 'Job created \u2014 contract ' +
            formatUsd(contract) +
            ' \u00b7 invoices auto-generated (40/30/30)',
        type: 'milestone',
      },
    ],
  });
  const { data, error } = await supabase
    .from('jobs')
    .insert([safe])
    .select(JOB_FIELDS)
    .single();
  if (error) throw error;
  // PQI: silently track job creation (fire-and-forget)
  pqi.trackJobCreated({ job: data });
  return data;
};

/* ----------------------------------------------------------------------------
 * Cost buckets
 *
 * Standing, tenant-scoped catch-all "jobs" for costs that don't belong to a
 * specific job — the Miscellaneous bucket (shop supplies, one-off store runs)
 * and the Gas / Fuel bucket (fuel driving between jobs). Each is a normal jobs
 * row (so it flows through job costing and the rebate tracker like any other),
 * just reserved by a fixed name with no client attached. We find-or-create them
 * lazily the first time something needs one, so no migration is required.
 * Unlike createJob(), buckets skip the auto-invoices and contract note — a
 * bucket has no contract and shouldn't spawn a billing schedule.
 *
 * Pickers show a sentinel option (e.g. '__MISC__') per bucket; resolveBucketJob
 * turns that sentinel into the real (created-on-demand) job at save time.
 * ------------------------------------------------------------------------- */
export const MISC_JOB_OPTION = '__MISC__';
export const GAS_JOB_OPTION = '__GAS__';

// The set of standing buckets. Add a row here to add a bucket everywhere.
export const JOB_BUCKETS = [
  {
    option: MISC_JOB_OPTION,
    name: 'Miscellaneous',
    label: 'Miscellaneous (no specific job)',
    note: 'Miscellaneous bucket — catch-all for costs not tied to a specific job',
  },
  {
    option: GAS_JOB_OPTION,
    name: 'Gas / Fuel',
    label: 'Gas / fuel (no specific job)',
    note: 'Gas / fuel bucket — fuel costs not tied to a specific job',
  },
];

// Back-compat alias.
export const MISC_JOB_NAME = JOB_BUCKETS[0].name;

// Is this picker value one of the bucket sentinels (vs a real job id)?
export const isBucketOption = (value) =>
  JOB_BUCKETS.some((b) => b.option === value);

// Human label for a bucket sentinel (the bucket's job name), else null.
export const bucketOptionName = (value) =>
  JOB_BUCKETS.find((b) => b.option === value)?.name || null;

// Does this job record look like one of our buckets (reserved name, no client)?
export const isBucketJob = (job) =>
  !!job &&
  !job.client_id &&
  JOB_BUCKETS.some((b) => b.name === String(job?.name || '').trim());

/**
 * Return the tenant's bucket job for a given name, creating it if it doesn't
 * exist yet. Safe to call repeatedly — it reuses the existing bucket.
 */
export const ensureBucketJob = async (name, note = '') => {
  // Reuse an existing bucket for this tenant (RLS scopes the query).
  const { data: existing, error: findErr } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .eq('name', name)
    .is('client_id', null)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (findErr) throw findErr;
  if (existing) return existing;

  const safe = sanitizeJobPayload({
    name,
    client: '',
    client_id: null,
    address: '',
    contract: 0,
    co_total: 0,
    phase: 0,
    status: 'active',
    started: 'TBD',
    est_end: 'TBD',
    job_type: 'fixed',
    materials_spent: 0,
    invoices: [],
    change_orders: [],
    notes: note
      ? [{ date: todayLabel(), text: note, type: 'milestone' }]
      : [],
    receipts: [],
    material_log: [],
  });
  const { data, error } = await supabase
    .from('jobs')
    .insert([safe])
    .select(JOB_FIELDS)
    .single();
  if (error) throw error;
  return data;
};

/**
 * Resolve a picker value to a job. If it's a bucket sentinel, find-or-create
 * that bucket; otherwise return null (caller should look it up as a real job).
 */
export const resolveBucketJob = async (option) => {
  const bucket = JOB_BUCKETS.find((b) => b.option === option);
  return bucket ? ensureBucketJob(bucket.name, bucket.note) : null;
};

// Back-compat: the Miscellaneous bucket specifically.
export const ensureMiscBucketJob = () =>
  ensureBucketJob(JOB_BUCKETS[0].name, JOB_BUCKETS[0].note);
export const isMiscBucketJob = (job) =>
  !!job && !job.client_id && String(job?.name || '').trim() === MISC_JOB_NAME;

/* ----------------------------------------------------------------------------
 * Phase advancement
 * ------------------------------------------------------------------------- */
export const advancePhase = async (job) => {
  if (!job?.id) throw new Error('Missing job id');
  const next = Math.min(PHASE_MAX_INDEX, (Number(job.phase) || 0) + 1);
  if (next === Number(job.phase)) return job;
  const phaseLabel = JOB_PHASES[next].label;
  const note = {
    date: todayLabel(),
    text: `Phase advanced \u2192 ${phaseLabel}`,
    type: 'milestone',
  };
  const updates = {
    phase: next,
    notes: [...(Array.isArray(job.notes) ? job.notes : []), note],
  };
  if (next === PHASE_MAX_INDEX) {
    updates.status = 'closed';
  }
  const updated = await updateJob(job.id, updates);
  // PQI: track phase progression (and job-closed if we just hit the final phase)
  pqi.trackJobPhaseAdvanced({ job: updated, fromPhase: Number(job.phase) || 0, toPhase: next });
  // Fire a margin snapshot capturing the cost/revenue posture at this phase.
  {
    const { actualCost, totalRevenue, marginPct } = computeEmbeddedJobMargin(updated);
    pqi.trackMarginSnapshot({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
      phasesCompleted: next,
      trigger: next === PHASE_MAX_INDEX ? 'job_closed' : 'phase_advanced',
    });
  }
  if (next === PHASE_MAX_INDEX) {
    const { actualCost, totalRevenue, marginPct } = computeEmbeddedJobMargin(updated);
    pqi.trackJobClosed({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
    });
  }
  return updated;
};

/* ----------------------------------------------------------------------------
 * Notes
 * ------------------------------------------------------------------------- */
export const addJobNote = async (job, { text, type = 'general' } = {}) => {
  if (!job?.id) throw new Error('Missing job id');
  if (!text?.trim()) throw new Error('Note text is required');
  const note = {
    date: todayLabel(),
    text: text.trim(),
    type: type || 'general',
  };
  const notes = [...(Array.isArray(job.notes) ? job.notes : []), note];
  return updateJob(job.id, { notes });
};

/* ----------------------------------------------------------------------------
 * Change orders
 * ------------------------------------------------------------------------- */
const recomputeCoTotal = (changeOrders = []) =>
  changeOrders.reduce((sum, co) => {
    if (co?.status === 'approved') {
      const n = Number(co?.amount) || 0;
      return sum + n;
    }
    return sum;
  }, 0);

export const addChangeOrder = async (
  job,
  { desc, amount, type = 'fixed', nte = null, notes = '' } = {}
) => {
  if (!job?.id) throw new Error('Missing job id');
  if (!desc?.trim()) throw new Error('Description is required');
  const isTM = type === 'tm';
  const newCo = {
    id: genId('CO'),
    desc: desc.trim(),
    // 'fixed' = flat contract add; 'tm' = time & material.
    type: isTM ? 'tm' : 'fixed',
    amount: Number(amount) || 0,
    // Optional not-to-exceed cap — T&M only. null = open-ended (no cap).
    nte: isTM && nte !== null && nte !== '' ? Number(nte) || 0 : null,
    notes: typeof notes === 'string' ? notes.trim() : '',
    status: 'pending',
    created: todayLabel(),
  };
  const change_orders = [...(Array.isArray(job.change_orders) ? job.change_orders : []), newCo];
  const co_total = recomputeCoTotal(change_orders);
  return updateJob(job.id, { change_orders, co_total });
};

/** Edit an existing change order's editable fields (patch is partial). */
export const editChangeOrder = async (job, coId, patch = {}) => {
  if (!job?.id) throw new Error('Missing job id');
  const change_orders = (Array.isArray(job.change_orders) ? job.change_orders : []).map(
    (co) => {
      if (co?.id !== coId) return co;
      const next = { ...co };
      if ('desc' in patch) next.desc = String(patch.desc || '').trim();
      if ('type' in patch) next.type = patch.type === 'tm' ? 'tm' : 'fixed';
      if ('amount' in patch) next.amount = Number(patch.amount) || 0;
      if ('nte' in patch)
        next.nte = patch.nte === null || patch.nte === '' ? null : Number(patch.nte) || 0;
      if ('notes' in patch) next.notes = String(patch.notes || '').trim();
      // A fixed CO never carries an NTE cap.
      if (next.type !== 'tm') next.nte = null;
      return next;
    }
  );
  const co_total = recomputeCoTotal(change_orders);
  return updateJob(job.id, { change_orders, co_total });
};

export const setChangeOrderStatus = async (job, coId, status) => {
  if (!job?.id) throw new Error('Missing job id');
  if (!['pending', 'approved', 'denied'].includes(status))
    throw new Error('Invalid CO status');
  const change_orders = (Array.isArray(job.change_orders) ? job.change_orders : []).map(
    (co) => (co?.id === coId ? { ...co, status } : co)
  );
  const co_total = recomputeCoTotal(change_orders);
  return updateJob(job.id, { change_orders, co_total });
};

export const removeChangeOrder = async (job, coId) => {
  if (!job?.id) throw new Error('Missing job id');
  const change_orders = (Array.isArray(job.change_orders) ? job.change_orders : []).filter(
    (co) => co?.id !== coId
  );
  const co_total = recomputeCoTotal(change_orders);
  return updateJob(job.id, { change_orders, co_total });
};

/* ----------------------------------------------------------------------------
 * Invoices
 * ------------------------------------------------------------------------- */
export const addInvoiceLine = async (job, { desc = 'New invoice', amount = 0, due = 'TBD' } = {}) => {
  if (!job?.id) throw new Error('Missing job id');
  const newInv = {
    id: genId('INV'),
    due,
    desc,
    amount: Number(amount) || 0,
    status: 'upcoming',
  };
  const invoices = [...(Array.isArray(job.invoices) ? job.invoices : []), newInv];
  return updateJob(job.id, { invoices });
};

export const updateInvoiceLine = async (job, invoiceId, updates = {}) => {
  if (!job?.id) throw new Error('Missing job id');
  const safe = {
    ...(updates.desc !== undefined ? { desc: String(updates.desc) } : {}),
    ...(updates.due !== undefined ? { due: String(updates.due) } : {}),
    ...(updates.amount !== undefined
      ? { amount: Number(updates.amount) || 0 }
      : {}),
    ...(updates.status !== undefined ? { status: String(updates.status) } : {}),
  };
  const invoices = (Array.isArray(job.invoices) ? job.invoices : []).map((inv) =>
    inv?.id === invoiceId ? { ...inv, ...safe } : inv
  );
  return updateJob(job.id, { invoices });
};

export const deleteInvoiceLine = async (job, invoiceId) => {
  if (!job?.id) throw new Error('Missing job id');
  const invoices = (Array.isArray(job.invoices) ? job.invoices : []).filter(
    (inv) => inv?.id !== invoiceId
  );
  return updateJob(job.id, { invoices });
};

export const markInvoicePaid = async (job, invoiceId) => {
  const inv = (Array.isArray(job?.invoices) ? job.invoices : []).find(
    (i) => i?.id === invoiceId
  );
  const updated = await updateInvoiceLine(job, invoiceId, { status: 'paid', paid_at: new Date().toISOString() });
  // PQI: track invoice payment (silent)
  pqi.trackInvoicePaid({ job, invoiceId, amount: Number(inv?.amount) || 0 });
  // PQI: margin snapshot — revenue collected posture changed
  {
    const { actualCost, totalRevenue, marginPct } = computeEmbeddedJobMargin(updated);
    pqi.trackMarginSnapshot({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
      trigger: 'invoice_paid',
    });
  }
  return updated;
};
